import { X } from 'lucide-react';

/**
 * Shared layout primitives for the admin pages — modal scaffolding, icon
 * buttons, table helpers. Kept here so AdminUsersPage and AdminTenantsPage
 * share one source of truth on spacing and colours.
 */

export function Modal({
  children, onClose,
}: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50"
         onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6"
           onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({
  icon, children, onClose,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <header className="flex items-center justify-between gap-3 mb-4">
      <h2 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
        {icon}
        {children}
      </h2>
      <button type="button" onClick={onClose}
              className="text-gray-400 hover:text-gray-600">
        <X className="w-5 h-5" aria-hidden />
      </button>
    </header>
  );
}

export function ModalActions({
  onCancel, confirmLabel, pending, danger, onConfirm,
}: {
  onCancel: () => void;
  confirmLabel: string;
  pending: boolean;
  danger?: boolean;
  onConfirm?: () => void;
}) {
  const confirmCls = danger
    ? 'bg-danger-500 hover:bg-danger-600'
    : 'bg-accent-alt-600 hover:bg-accent-alt-700';
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100
                         rounded-md transition-colors">
        Cancel
      </button>
      <button type={onConfirm ? 'button' : 'submit'}
              onClick={onConfirm}
              disabled={pending}
              className={`px-3 py-1.5 text-sm font-medium text-white rounded-md
                          disabled:opacity-50 transition-colors ${confirmCls}`}>
        {confirmLabel}
      </button>
    </div>
  );
}

export function IconButton({
  title, danger, disabled, onClick, children,
}: {
  title: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" title={title} disabled={disabled} onClick={onClick}
            className={'inline-flex items-center justify-center w-7 h-7 rounded-md '
                     + 'transition-colors disabled:opacity-30 disabled:cursor-not-allowed '
                     + (danger
                          ? 'text-danger-500 hover:bg-danger-50'
                          : 'text-gray-500 hover:bg-gray-100')}>
      {children}
    </button>
  );
}

export const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent ' +
  'disabled:bg-gray-50 disabled:cursor-not-allowed';

export function Field({
  label, children,
}: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

export function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 text-left font-medium">{children}</th>;
}
export function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-2.5 text-sm text-gray-800">{children}</td>;
}
